export type DealerInventoryType = "used_retail" | "retail_wholesale" | "wholesale" | "bhph" | "franchise";

export const INVENTORY_TYPE_LABELS: Record<DealerInventoryType, string> = {
  used_retail: "Retail",
  retail_wholesale: "Retail/Wholesale",
  wholesale: "Wholesale",
  bhph: "Buy Here Pay Here",
  franchise: "Franchise",
};

export interface BenchmarkSet {
  // Phase 1
  numRooftops: number;
  avgUnitsOnLot: number;
  lotCapacity: number;
  totalHeadcount: number;
  salesHeadcount: number;
  fiHeadcount: number;
  serviceReconHeadcount: number;
  adminHeadcount: number;
  // Phase 2
  dmsSystem: string;
  dmsMonthlyCost: number;
  defaultVendors: Array<{ name: string; category: string; monthlyCost: number }>;
  // Phase 3
  avgSalesPerMonth: number;
  lendingPrimePct: number;
  lendingSubprimePct: number;
  lendingBhphPct: number;
  lendingCashPct: number;
  // Phase 4
  avgFrontEndGross: number;
  avgBackEndGross: number;
  avgFixedCosts: number;
  avgVariableCostPerUnit: number;
  avgReconCost: number;
  avgTransportFee: number;
  // Phase 5
  avgFloorplanRate: number;
  targetTurnDays: number;
  // Phase 6
  sourcingAuctionPct: number;
  sourcingTradeInPct: number;
  sourcingPrivatePartyPct: number;
  sourcingDealerToDealerPct: number;
  avgDaysPurchaseToFrontline: number;
  // Phase 7 — range defaults
  cashReservesRange: string;
  // Phase 8
  monthlyLeadVolume: number;
  leadToSaleConversionPct: number;
}

const BENCHMARKS: Record<DealerInventoryType, BenchmarkSet> = {
  retail_wholesale: {
    numRooftops: 1,
    avgUnitsOnLot: 50,
    lotCapacity: 65,
    totalHeadcount: 13,
    salesHeadcount: 4,
    fiHeadcount: 1,
    serviceReconHeadcount: 4,
    adminHeadcount: 4,
    dmsSystem: "DealerSocket",
    dmsMonthlyCost: 650,
    defaultVendors: [
      { name: "Autotrader", category: "marketing", monthlyCost: 1000 },
      { name: "Cars.com", category: "marketing", monthlyCost: 800 },
      { name: "Manheim", category: "marketing", monthlyCost: 500 },
    ],
    avgSalesPerMonth: 32,
    lendingPrimePct: 15,
    lendingSubprimePct: 35,
    lendingBhphPct: 20,
    lendingCashPct: 30,
    avgFrontEndGross: 2600,
    avgBackEndGross: 500,
    avgFixedCosts: 26000,
    avgVariableCostPerUnit: 425,
    avgReconCost: 1000,
    avgTransportFee: 375,
    avgFloorplanRate: 18.8,
    targetTurnDays: 32,
    sourcingAuctionPct: 60,
    sourcingTradeInPct: 20,
    sourcingPrivatePartyPct: 12,
    sourcingDealerToDealerPct: 8,
    avgDaysPurchaseToFrontline: 6,
    cashReservesRange: "50-150k",
    monthlyLeadVolume: 160,
    leadToSaleConversionPct: 18,
  },
  used_retail: {
    numRooftops: 1,
    avgUnitsOnLot: 45,
    lotCapacity: 60,
    totalHeadcount: 12,
    salesHeadcount: 4,
    fiHeadcount: 1,
    serviceReconHeadcount: 4,
    adminHeadcount: 3,
    dmsSystem: "DealerSocket",
    dmsMonthlyCost: 650,
    defaultVendors: [
      { name: "Autotrader", category: "marketing", monthlyCost: 1200 },
      { name: "Cars.com", category: "marketing", monthlyCost: 900 },
      { name: "CarGurus", category: "marketing", monthlyCost: 800 },
    ],
    avgSalesPerMonth: 30,
    lendingPrimePct: 15,
    lendingSubprimePct: 40,
    lendingBhphPct: 25,
    lendingCashPct: 20,
    avgFrontEndGross: 2800,
    avgBackEndGross: 600,
    avgFixedCosts: 28000,
    avgVariableCostPerUnit: 450,
    avgReconCost: 1100,
    avgTransportFee: 350,
    avgFloorplanRate: 18.5,
    targetTurnDays: 35,
    sourcingAuctionPct: 55,
    sourcingTradeInPct: 25,
    sourcingPrivatePartyPct: 15,
    sourcingDealerToDealerPct: 5,
    avgDaysPurchaseToFrontline: 7,
    cashReservesRange: "50-150k",
    monthlyLeadVolume: 180,
    leadToSaleConversionPct: 17,
  },
  franchise: {
    numRooftops: 1,
    avgUnitsOnLot: 120,
    lotCapacity: 150,
    totalHeadcount: 42,
    salesHeadcount: 12,
    fiHeadcount: 3,
    serviceReconHeadcount: 18,
    adminHeadcount: 9,
    dmsSystem: "CDK Global",
    dmsMonthlyCost: 2200,
    defaultVendors: [
      { name: "Autotrader", category: "marketing", monthlyCost: 3500 },
      { name: "Cars.com", category: "marketing", monthlyCost: 2000 },
      { name: "Google Ads", category: "marketing", monthlyCost: 4000 },
    ],
    avgSalesPerMonth: 80,
    lendingPrimePct: 55,
    lendingSubprimePct: 30,
    lendingBhphPct: 2,
    lendingCashPct: 13,
    avgFrontEndGross: 1900,
    avgBackEndGross: 1800,
    avgFixedCosts: 95000,
    avgVariableCostPerUnit: 350,
    avgReconCost: 600,
    avgTransportFee: 200,
    avgFloorplanRate: 14.5,
    targetTurnDays: 28,
    sourcingAuctionPct: 20,
    sourcingTradeInPct: 55,
    sourcingPrivatePartyPct: 5,
    sourcingDealerToDealerPct: 20,
    avgDaysPurchaseToFrontline: 5,
    cashReservesRange: "150-500k",
    monthlyLeadVolume: 450,
    leadToSaleConversionPct: 18,
  },
  wholesale: {
    numRooftops: 1,
    avgUnitsOnLot: 50,
    lotCapacity: 70,
    totalHeadcount: 10,
    salesHeadcount: 3,
    fiHeadcount: 1,
    serviceReconHeadcount: 3,
    adminHeadcount: 3,
    dmsSystem: "DealerSocket",
    dmsMonthlyCost: 500,
    defaultVendors: [
      { name: "Manheim", category: "marketing", monthlyCost: 600 },
      { name: "ADESA", category: "marketing", monthlyCost: 500 },
    ],
    avgSalesPerMonth: 35,
    lendingPrimePct: 10,
    lendingSubprimePct: 35,
    lendingBhphPct: 20,
    lendingCashPct: 35,
    avgFrontEndGross: 2200,
    avgBackEndGross: 400,
    avgFixedCosts: 22000,
    avgVariableCostPerUnit: 400,
    avgReconCost: 900,
    avgTransportFee: 400,
    avgFloorplanRate: 19.0,
    targetTurnDays: 25,
    sourcingAuctionPct: 70,
    sourcingTradeInPct: 10,
    sourcingPrivatePartyPct: 10,
    sourcingDealerToDealerPct: 10,
    avgDaysPurchaseToFrontline: 5,
    cashReservesRange: "50-150k",
    monthlyLeadVolume: 120,
    leadToSaleConversionPct: 29,
  },
  bhph: {
    numRooftops: 1,
    avgUnitsOnLot: 40,
    lotCapacity: 55,
    totalHeadcount: 10,
    salesHeadcount: 3,
    fiHeadcount: 1,
    serviceReconHeadcount: 3,
    adminHeadcount: 3,
    dmsSystem: "DealerCenter",
    dmsMonthlyCost: 500,
    defaultVendors: [
      { name: "Cars.com", category: "marketing", monthlyCost: 600 },
      { name: "Facebook Ads", category: "marketing", monthlyCost: 800 },
    ],
    avgSalesPerMonth: 25,
    lendingPrimePct: 0,
    lendingSubprimePct: 10,
    lendingBhphPct: 80,
    lendingCashPct: 10,
    avgFrontEndGross: 3200,
    avgBackEndGross: 200,
    avgFixedCosts: 20000,
    avgVariableCostPerUnit: 450,
    avgReconCost: 1000,
    avgTransportFee: 300,
    avgFloorplanRate: 20.0,
    targetTurnDays: 40,
    sourcingAuctionPct: 50,
    sourcingTradeInPct: 20,
    sourcingPrivatePartyPct: 25,
    sourcingDealerToDealerPct: 5,
    avgDaysPurchaseToFrontline: 8,
    cashReservesRange: "50-150k",
    monthlyLeadVolume: 150,
    leadToSaleConversionPct: 17,
  },
};

export function getBenchmarks(type: string = "used_retail"): BenchmarkSet {
  // Map legacy values to new types
  const mapped: Record<string, DealerInventoryType> = {
    used: "used_retail",
    mixed: "used_retail",
    retail: "used_retail",
    used_retail: "used_retail",
    retail_wholesale: "retail_wholesale",
    wholesale: "wholesale",
    bhph: "bhph",
    franchise: "franchise",
  };
  const resolved = mapped[type] ?? "used_retail";
  return BENCHMARKS[resolved];
}

export interface BenchmarkRange {
  low: number;
  median: number;
  high: number;
  p90: number;
  unit?: string;
}

export interface StaffingBenchmarkRange {
  low: number;
  median: number;
  high: number;
  unit: string;
  label: string;
  description: string;
  higherIsBetter: boolean;
}

export const STAFFING_BENCHMARKS: Record<
  "revenuePerEmployee" | "unitsPerSalesperson" | "grossPerFI" | "costPerEmployee",
  Record<DealerInventoryType, StaffingBenchmarkRange>
> = {
  revenuePerEmployee: {
    used_retail: { low: 25000, median: 45000, high: 70000, unit: "$", label: "Revenue per Employee / Mo", description: "Monthly revenue divided by total headcount", higherIsBetter: true },
    retail_wholesale: { low: 23000, median: 42000, high: 66000, unit: "$", label: "Revenue per Employee / Mo", description: "Monthly revenue divided by total headcount", higherIsBetter: true },
    wholesale: { low: 20000, median: 38000, high: 60000, unit: "$", label: "Revenue per Employee / Mo", description: "Monthly revenue divided by total headcount", higherIsBetter: true },
    bhph: { low: 18000, median: 35000, high: 55000, unit: "$", label: "Revenue per Employee / Mo", description: "Monthly revenue divided by total headcount", higherIsBetter: true },
    franchise: { low: 40000, median: 65000, high: 100000, unit: "$", label: "Revenue per Employee / Mo", description: "Monthly revenue divided by total headcount", higherIsBetter: true },
  },
  unitsPerSalesperson: {
    used_retail: { low: 5, median: 8, high: 14, unit: "units", label: "Units Sold / Salesperson / Mo", description: "Average monthly units sold per sales staff member", higherIsBetter: true },
    retail_wholesale: { low: 6, median: 9, high: 16, unit: "units", label: "Units Sold / Salesperson / Mo", description: "Average monthly units sold per sales staff member", higherIsBetter: true },
    wholesale: { low: 8, median: 12, high: 20, unit: "units", label: "Units Sold / Salesperson / Mo", description: "Average monthly units sold per sales staff member", higherIsBetter: true },
    bhph: { low: 4, median: 7, high: 12, unit: "units", label: "Units Sold / Salesperson / Mo", description: "Average monthly units sold per sales staff member", higherIsBetter: true },
    franchise: { low: 6, median: 9, high: 15, unit: "units", label: "Units Sold / Salesperson / Mo", description: "Average monthly units sold per sales staff member", higherIsBetter: true },
  },
  grossPerFI: {
    used_retail: { low: 8000, median: 25000, high: 45000, unit: "$", label: "Gross Profit / F&I Manager / Mo", description: "Gross profit generated per F&I manager per month", higherIsBetter: true },
    retail_wholesale: { low: 5000, median: 15000, high: 30000, unit: "$", label: "Gross Profit / F&I Manager / Mo", description: "Gross profit generated per F&I manager per month", higherIsBetter: true },
    wholesale: { low: 3000, median: 10000, high: 22000, unit: "$", label: "Gross Profit / F&I Manager / Mo", description: "Gross profit generated per F&I manager per month", higherIsBetter: true },
    bhph: { low: 2000, median: 6000, high: 14000, unit: "$", label: "Gross Profit / F&I Manager / Mo", description: "Gross profit generated per F&I manager per month", higherIsBetter: true },
    franchise: { low: 30000, median: 60000, high: 100000, unit: "$", label: "Gross Profit / F&I Manager / Mo", description: "Gross profit generated per F&I manager per month", higherIsBetter: true },
  },
  costPerEmployee: {
    used_retail: { low: 2500, median: 4500, high: 7000, unit: "$", label: "Operating Cost / Employee / Mo", description: "Monthly operating expenses divided by total headcount", higherIsBetter: false },
    retail_wholesale: { low: 2300, median: 4200, high: 6500, unit: "$", label: "Operating Cost / Employee / Mo", description: "Monthly operating expenses divided by total headcount", higherIsBetter: false },
    wholesale: { low: 2000, median: 3800, high: 6000, unit: "$", label: "Operating Cost / Employee / Mo", description: "Monthly operating expenses divided by total headcount", higherIsBetter: false },
    bhph: { low: 2000, median: 3500, high: 5500, unit: "$", label: "Operating Cost / Employee / Mo", description: "Monthly operating expenses divided by total headcount", higherIsBetter: false },
    franchise: { low: 3500, median: 6000, high: 10000, unit: "$", label: "Operating Cost / Employee / Mo", description: "Monthly operating expenses divided by total headcount", higherIsBetter: false },
  },
};

export const BENCHMARK_RANGES: Record<string, Record<DealerInventoryType, BenchmarkRange>> = {
  avgFrontEndGross: {
    used_retail: { low: 1200, median: 2800, high: 4200, p90: 5500, unit: "$" },
    retail_wholesale: { low: 1100, median: 2600, high: 4000, p90: 5200, unit: "$" },
    wholesale: { low: 1000, median: 2200, high: 3500, p90: 5000, unit: "$" },
    bhph: { low: 1500, median: 3000, high: 4500, p90: 6000, unit: "$" },
    franchise: { low: 800, median: 1900, high: 3200, p90: 4500, unit: "$" },
  },
  avgBackEndGross: {
    used_retail: { low: 0, median: 600, high: 1500, p90: 2200, unit: "$" },
    retail_wholesale: { low: 0, median: 500, high: 1300, p90: 2000, unit: "$" },
    wholesale: { low: 0, median: 400, high: 1000, p90: 1800, unit: "$" },
    bhph: { low: 0, median: 200, high: 600, p90: 1200, unit: "$" },
    franchise: { low: 600, median: 1800, high: 3500, p90: 5000, unit: "$" },
  },
  avgSalesPerMonth: {
    used_retail: { low: 10, median: 30, high: 60, p90: 100, unit: "units" },
    retail_wholesale: { low: 12, median: 32, high: 65, p90: 110, unit: "units" },
    wholesale: { low: 15, median: 35, high: 70, p90: 120, unit: "units" },
    bhph: { low: 8, median: 25, high: 50, p90: 80, unit: "units" },
    franchise: { low: 30, median: 80, high: 160, p90: 250, unit: "units" },
  },
  avgReconCost: {
    used_retail: { low: 400, median: 1100, high: 2000, p90: 3000, unit: "$" },
    retail_wholesale: { low: 350, median: 1000, high: 1800, p90: 2800, unit: "$" },
    wholesale: { low: 200, median: 700, high: 1400, p90: 2200, unit: "$" },
    bhph: { low: 300, median: 900, high: 1800, p90: 2800, unit: "$" },
    franchise: { low: 200, median: 600, high: 1200, p90: 2000, unit: "$" },
  },
  leadToSaleConversionPct: {
    used_retail: { low: 8, median: 17, high: 28, p90: 40, unit: "%" },
    retail_wholesale: { low: 9, median: 18, high: 29, p90: 42, unit: "%" },
    wholesale: { low: 20, median: 30, high: 45, p90: 60, unit: "%" },
    bhph: { low: 10, median: 18, high: 30, p90: 45, unit: "%" },
    franchise: { low: 10, median: 18, high: 30, p90: 45, unit: "%" },
  },
  avgFloorplanRate: {
    used_retail: { low: 12, median: 18.5, high: 24, p90: 30, unit: "%" },
    retail_wholesale: { low: 12, median: 18.8, high: 24.5, p90: 31, unit: "%" },
    wholesale: { low: 14, median: 20, high: 26, p90: 32, unit: "%" },
    bhph: { low: 15, median: 21, high: 28, p90: 35, unit: "%" },
    franchise: { low: 10, median: 14.5, high: 20, p90: 25, unit: "%" },
  },
  targetTurnDays: {
    used_retail: { low: 20, median: 35, high: 50, p90: 65, unit: "days" },
    retail_wholesale: { low: 18, median: 32, high: 48, p90: 62, unit: "days" },
    wholesale: { low: 10, median: 22, high: 35, p90: 50, unit: "days" },
    bhph: { low: 25, median: 42, high: 60, p90: 80, unit: "days" },
    franchise: { low: 15, median: 28, high: 45, p90: 60, unit: "days" },
  },
};

export const DMS_OPTIONS = [
  { label: "CDK Global", avgCost: 2200 },
  { label: "DealerSocket", avgCost: 1050 },
  { label: "Reynolds & Reynolds", avgCost: 1800 },
  { label: "Dealertrack DMS", avgCost: 900 },
  { label: "Tekion", avgCost: 1400 },
  { label: "DealerCenter", avgCost: 500 },
  { label: "Frazer", avgCost: 200 },
  { label: "Adam Systems", avgCost: 400 },
  { label: "Other", avgCost: null },
];

export const MARKETING_PLATFORM_OPTIONS = [
  { label: "Autotrader", avgCost: 2000 },
  { label: "Cars.com", avgCost: 1400 },
  { label: "CarGurus", avgCost: 1000 },
  { label: "CarFax", avgCost: 600 },
  { label: "Facebook Ads", avgCost: 1200 },
  { label: "Google Ads", avgCost: 3000 },
  { label: "TrueCar", avgCost: 800 },
  { label: "AutoUSA", avgCost: 700 },
  { label: "Lotlinx", avgCost: 1500 },
  { label: "Other", avgCost: 0 },
];
